/**
 * ComposerOverlays
 * ---
 * Extracted from `src/pages/Composer.tsx` to keep the page module under our
 * size budget. Hosts the four overlay surfaces that the Composer page mounts:
 *   - DispatchDetailDrawer (per-dispatch detail view)
 *   - BriefErrorDialog (consolidated error retry surface)
 *   - ContextSnapshotViewer (read-only AI-context snapshot inspector)
 *   - PendingDispatchDialog (confirm-before-send sheet)
 *
 * All state still lives on the page; this component is a pure consumer of the
 * snapshot + per-overlay coordinates the page passes in.
 */
import { DispatchDetailDrawer } from "./DispatchDetailDrawer";
import { BriefErrorDialog } from "./BriefErrorDialog";
import { ContextSnapshotViewer } from "./context/ContextSnapshotViewer";
import { PendingDispatchDialog } from "./panels/PendingDispatchDialog";
import type { Snapshot, PendingDispatch } from "./composerPageHelpers";
import type { ContentBriefPlan, ContentBriefDraft, ContentPublishingDispatch } from "@/lib/composerCore";

type Props = {
  workspaceId: string | null;
  snapshot: Snapshot;
  refresh: () => Promise<void> | void;
  canDispatch: boolean;

  // Dispatch drawer
  drawerDispatchId: string | null;
  setDrawerDispatchId: (v: string | null) => void;
  resendingDispatchId: string | null;
  onResendToFailed: (dispatch: ContentPublishingDispatch, recipients: string[]) => Promise<void> | void;

  // Error dialog
  errorBriefId: string | null;
  setErrorBriefId: (v: string | null) => void;
  latestPlanByBriefId: Map<string, ContentBriefPlan | null>;
  latestDraftByBriefId: Map<string, ContentBriefDraft | null>;

  // Context snapshot viewer
  snapshotViewer: { contextSnapshot: unknown; runLabel: string } | null;
  setSnapshotViewer: (v: { contextSnapshot: unknown; runLabel: string } | null) => void;

  // Pending dispatch confirm
  pendingDispatch: PendingDispatch | null;
  setPendingDispatch: (v: PendingDispatch | null) => void;
  dispatchingPubId: string | null;
  onConfirmDispatch: (publicationId: string) => void;
};

export const ComposerOverlays = ({
  workspaceId,
  snapshot,
  refresh,
  canDispatch,
  drawerDispatchId,
  setDrawerDispatchId,
  resendingDispatchId,
  onResendToFailed,
  errorBriefId,
  setErrorBriefId,
  latestPlanByBriefId,
  latestDraftByBriefId,
  snapshotViewer,
  setSnapshotViewer,
  pendingDispatch,
  setPendingDispatch,
  dispatchingPubId,
  onConfirmDispatch,
}: Props) => {
  // --- Dispatch drawer lookups ---
  const dispatch = drawerDispatchId
    ? snapshot.publishingDispatches.find((d) => d.id === drawerDispatchId) ?? null
    : null;
  const drawerConnector = dispatch
    ? snapshot.publishingConnectors.find((c) => c.id === dispatch.connector_id) ?? null
    : null;
  const drawerPub = dispatch
    ? snapshot.briefPublications.find((p) => p.id === dispatch.publication_id) ?? null
    : null;

  // --- Error dialog lookups ---
  const errorBrief = errorBriefId ? snapshot.briefs.find((b) => b.id === errorBriefId) ?? null : null;
  const latestPlan = errorBrief ? latestPlanByBriefId.get(errorBrief.id) ?? null : null;
  const latestDraft = errorBrief ? latestDraftByBriefId.get(errorBrief.id) ?? null : null;
  const failedPubs = errorBrief
    ? snapshot.briefPublications.filter((p) => p.piece_id === errorBrief.id && p.status === "failed")
    : [];
  const failedDispatches = errorBrief
    ? snapshot.publishingDispatches.filter(
        (d) =>
          d.status === "failed" &&
          snapshot.briefPublications.some((p) => p.id === d.publication_id && p.piece_id === errorBrief.id),
      )
    : [];

  return (
    <>
      <DispatchDetailDrawer
        open={Boolean(drawerDispatchId)}
        onOpenChange={(open) => { if (!open) setDrawerDispatchId(null); }}
        dispatch={dispatch}
        connector={drawerConnector}
        publicationTitle={drawerPub?.prepared_title ?? null}
        onResendToFailed={onResendToFailed}
        resending={dispatch ? resendingDispatchId === dispatch.id : false}
        canResend={
          canDispatch &&
          !!drawerConnector &&
          drawerConnector.status === "active" &&
          !!drawerPub &&
          drawerPub.status === "succeeded"
        }
      />

      <BriefErrorDialog
        open={Boolean(errorBriefId)}
        onOpenChange={(open) => { if (!open) setErrorBriefId(null); }}
        workspaceId={workspaceId}
        brief={errorBrief}
        latestPlan={latestPlan}
        latestDraft={latestDraft}
        failedPublications={failedPubs}
        failedDispatches={failedDispatches}
        onRetried={refresh}
      />

      {workspaceId ? (
        <ContextSnapshotViewer
          open={snapshotViewer !== null}
          onOpenChange={(open) => { if (!open) setSnapshotViewer(null); }}
          workspaceId={workspaceId}
          contextSnapshot={snapshotViewer?.contextSnapshot ?? null}
          runLabel={snapshotViewer?.runLabel ?? ""}
        />
      ) : null}

      <PendingDispatchDialog
        pendingDispatch={pendingDispatch}
        dispatchingPubId={dispatchingPubId}
        onCancel={() => setPendingDispatch(null)}
        onConfirm={(publicationId) => onConfirmDispatch(publicationId)}
      />
    </>
  );
};
